import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Award, Clock, Shield, Users } from 'lucide-react';
import { useSettings } from '@/context/SettingsContext';

const WhyUs = () => {
  const { settings, loading } = useSettings();

  const strengths = [
    {
      icon: <Award size={32} />,
      title: 'Superior Quality',
      description: 'We use premium materials and follow strict quality standards in every project.',
    },
    {
      icon: <Clock size={32} />,
      title: 'Timely Delivery',
      description: 'Our track record speaks for itself - on-time project completion, every time.',
    },
    {
      icon: <Shield size={32} />,
      title: 'Transparent Process',
      description: 'Complete transparency in pricing, timelines, and project updates.',
    },
    {
      icon: <Users size={32} />,
      title: 'Customer First',
      description: 'Dedicated support team to guide you through every step of your home buying journey.',
    },
  ];

  // Get dynamic content from settings with fallbacks
  const pageTitle = settings.why_us_title || 'Why Choose Infinity Group?';
  const pageDescription = settings.why_us_description || 'With over a decade of experience in building quality residential apartments in Nagpur, we\'ve earned the trust of thousands of families.';
  const mainContent = settings.why_us_content || 'Founded in 2010, Infinity Group has been at the forefront of residential real estate development in Nagpur. With a commitment to excellence and innovation, we\'ve successfully delivered over 25 projects, providing homes to more than 500 families.';
  const founderMessage = settings.founder_message || 'At Infinity Group, we don\'t just build apartments; we build dreams. Every project is a testament to our commitment to quality, transparency, and customer satisfaction in Nagpur.';
  const founders = settings.founders && settings.founders.length > 0 
    ? settings.founders 
    : [{ name: 'Mr Ashay Nalamwar', title: 'Co-Founder' }];
  
  const primaryColor = settings.primary_color || '#C41E3A';
  const secondaryColor = settings.secondary_color || '#8B2635';

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 text-center" data-testid="why-us-heading">
            {pageTitle}
          </h1>
          <p className="text-base sm:text-lg text-gray-600 text-center max-w-3xl mx-auto mb-12" data-testid="why-us-description">
            {pageDescription}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {strengths.map((strength, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-md" data-testid={`strength-card-${index}`}>
                <div style={{ color: primaryColor }} className="mb-4">{strength.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{strength.title}</h3>
                <p className="text-gray-600 leading-relaxed">{strength.description}</p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-xl p-8 lg:p-12 shadow-md">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">About Infinity Group</h2>
            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p data-testid="why-us-content">
                {mainContent}
              </p>
              <p>
                Our journey began with a simple vision: to create quality homes that people can afford and be proud of. Today, we continue to uphold that vision with every project we undertake in Nagpur, Maharashtra.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mt-8 pt-8 border-t">
                <div className="text-center">
                  <p className="text-4xl font-bold mb-2" style={{ color: primaryColor }} data-testid="stat-1-number">{settings.stat_1_number || '15+'}</p>
                  <p className="text-sm text-gray-600" data-testid="stat-1-label">{settings.stat_1_label || 'Years of Experience'}</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold mb-2" style={{ color: primaryColor }} data-testid="stat-2-number">{settings.stat_2_number || '25+'}</p>
                  <p className="text-sm text-gray-600" data-testid="stat-2-label">{settings.stat_2_label || 'Projects Delivered'}</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold mb-2" style={{ color: primaryColor }} data-testid="stat-3-number">{settings.stat_3_number || '500+'}</p>
                  <p className="text-sm text-gray-600" data-testid="stat-3-label">{settings.stat_3_label || 'Happy Families'}</p>
                </div>
              </div>
            </div>
          </div>

          <div 
            className="mt-12 rounded-xl p-8 lg:p-12 text-white text-center" 
            style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` }}
            data-testid="founder-message-section"
          >
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">A Message from Our Founders</h2>
            <p className="text-base sm:text-lg leading-relaxed max-w-3xl mx-auto" data-testid="founder-message">
              "{founderMessage}"
            </p>
            <div className="mt-6 space-y-2" data-testid="founders-list">
              {founders.map((founder, index) => (
                <div key={index}>
                  <p className="font-semibold">
                    - {founder.name}, {founder.title}
                  </p>
                  {founder.qualification && (
                    <p className="text-sm opacity-90 ml-3" data-testid={`founder-qualification-${index}`}>
                      {founder.qualification}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WhyUs;